/**
 * Delayed Scripts Manager
 *
 * Handles delayed loading of third-party scripts to improve page speed.
 * Supports multiple delay strategies: idle, interaction, time-based, and immediate.
 *
 * @version 1.0.0
 */

class DelayedScriptsManager {
  constructor() {
    this.scripts = [];
    this.hasInteracted = false;
    this.interactionEvents = ['scroll', 'mousemove', 'touchstart', 'click', 'keydown'];
    this.loadedScripts = new Set();
  }

  /**
   * Register a script to be loaded with delay
   * @param {Object} config - Script configuration
   * @param {string} config.id - Unique identifier for the script
   * @param {string} config.strategy - Delay strategy: 'idle', 'interaction', 'time', 'immediate'
   * @param {number} config.delay - Delay time in milliseconds (for 'time' strategy)
   * @param {string} config.src - Script source URL (optional)
   * @param {string} config.code - Inline script code (optional)
   * @param {Function} config.onLoad - Callback after script loads (optional)
   */
  register(config) {
    if (!config.id) {
      console.error('DelayedScriptsManager: Script ID is required');
      return;
    }

    // Avoid duplicate registration
    if (this.scripts.find(s => s.id === config.id)) {
      console.warn(`DelayedScriptsManager: Script "${config.id}" already registered`);
      return;
    }

    this.scripts.push({
      id: config.id,
      strategy: config.strategy || 'idle',
      delay: config.delay || 3000,
      src: config.src || null,
      code: config.code || null,
      onLoad: config.onLoad || null,
      loaded: false
    });
  }

  /**
   * Initialize and start loading scripts based on their strategies
   */
  init() {
    this.scripts.forEach(script => {
      switch (script.strategy) {
        case 'immediate':
          this.loadScript(script);
          break;

        case 'idle':
          this.loadOnIdle(script);
          break;

        case 'interaction':
          this.loadOnInteraction(script);
          break;

        case 'time':
          this.loadOnTime(script);
          break;

        default:
          console.warn(`DelayedScriptsManager: Unknown strategy "${script.strategy}" for script "${script.id}". Defaulting to idle.`);
          this.loadOnIdle(script);
      }
    });
  }

  /**
   * Load script immediately (but still async)
   */
  loadScript(script) {
    if (script.loaded || this.loadedScripts.has(script.id)) {
      return;
    }

    script.loaded = true;
    this.loadedScripts.add(script.id);

    if (script.src) {
      // External script
      const scriptElement = document.createElement('script');
      scriptElement.src = script.src;
      scriptElement.async = true;
      scriptElement.dataset.delayedScript = script.id;

      if (script.onLoad) {
        scriptElement.onload = script.onLoad;
      }

      document.body.appendChild(scriptElement);
    } else if (script.code) {
      // Inline script
      const scriptElement = document.createElement('script');
      scriptElement.textContent = script.code;
      scriptElement.dataset.delayedScript = script.id;
      document.body.appendChild(scriptElement);

      if (script.onLoad) {
        script.onLoad();
      }
    }

    // Script loaded successfully
  }

  /**
   * Load script when browser is idle
   */
  loadOnIdle(script) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        this.loadScript(script);
      }, { timeout: 5000 });
    } else {
      // Fallback for browsers that don't support requestIdleCallback
      setTimeout(() => {
        this.loadScript(script);
      }, 2000);
    }
  }

  /**
   * Load script after user interaction
   */
  loadOnInteraction(script) {
    // If user already interacted, load immediately
    if (this.hasInteracted) {
      this.loadScript(script);
      return;
    }

    // Set up one-time listeners for interaction events
    const loadOnce = () => {
      this.hasInteracted = true;
      this.loadScript(script);

      // Remove all listeners after first interaction
      this.interactionEvents.forEach(event => {
        window.removeEventListener(event, loadOnce, { passive: true });
      });
    };

    // Add listeners for all interaction events
    this.interactionEvents.forEach(event => {
      window.addEventListener(event, loadOnce, { passive: true, once: true });
    });

    // Fallback: load after delay anyway if no interaction
    setTimeout(() => {
      if (!script.loaded) {
        loadOnce();
      }
    }, script.delay);
  }

  /**
   * Load script after specified time delay
   */
  loadOnTime(script) {
    setTimeout(() => {
      this.loadScript(script);
    }, script.delay);
  }

  /**
   * Load all scripts immediately (emergency override)
   */
  loadAll() {
    this.scripts.forEach(script => {
      this.loadScript(script);
    });
  }
}

// Create global instance
window.delayedScripts = new DelayedScriptsManager();

// Auto-initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.delayedScripts.init();
  });
} else {
  // DOM already loaded
  window.delayedScripts.init();
}
